import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { EffectEditor, EMPTY_EFFECT, SKILLS } from "./ObstacleCreateDialog";
import { Flame, EyeOff } from "lucide-react";

const SAVE_TYPES = ["reflex", "fortitude", "will"];
const CHECK_KINDS = [
  { value: "save", label: "Saving Throw" },
  { value: "skill", label: "Skill Check" },
  { value: "flat", label: "Flat DC (no modifier)" },
];

export default function TemplateEditDialog({ open, onOpenChange, gameId, template, canEditEffect, gridSize, onSaved, onDeleted }) {
  const [label, setLabel] = useState("");
  const [sizeFt, setSizeFt] = useState(20);
  const [rotation, setRotation] = useState(0);
  const [dc, setDc] = useState(15);
  const [saveType, setSaveType] = useState("reflex");
  const [checkKind, setCheckKind] = useState("save");
  const [skill, setSkill] = useState("athletics");
  const [successEffect, setSuccessEffect] = useState(EMPTY_EFFECT);
  const [failEffect, setFailEffect] = useState(EMPTY_EFFECT);
  const [hidden, setHidden] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [spells, setSpells] = useState([]);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
    api.get("/pf2e/spells").then((r) => setSpells(r.data)).catch(() => setSpells([]));
  }, []);

  useEffect(() => {
    if (!template) return;
    setLabel(template.label || "");
    setSizeFt(Math.round(((template.size || 100) / gridSize) * 5));
    setRotation(Math.round(template.rotation || 0));
    setDc(template.dc ?? 15);
    setSaveType(template.save_type || "reflex");
    setCheckKind(template.check_kind || "save");
    setSkill(template.skill || "athletics");
    setSuccessEffect({ ...EMPTY_EFFECT, ...(template.success_effect || {}), message: template.success_effect?.message || "", damage_dice: template.success_effect?.damage_dice || "", damage_type: template.success_effect?.damage_type || "" });
    setFailEffect({ ...EMPTY_EFFECT, ...(template.fail_effect || {}), message: template.fail_effect?.message || "", damage_dice: template.fail_effect?.damage_dice || "", damage_type: template.fail_effect?.damage_type || "" });
    setHidden(!!template.hidden);
  }, [template, gridSize]);

  if (!template) return null;

  const applySpellPreset = (slug) => {
    const spell = spells.find((s) => s.slug === slug);
    if (!spell) return;
    setSaveType(spell.save || "reflex");
    setCheckKind("save");
    setLabel(spell.name);
    if (spell.damage) {
      setFailEffect((f) => ({ ...f, damage_dice: spell.damage.dice || "", damage_type: spell.damage.type || "" }));
    }
    toast.info(`Loaded ${spell.name}'s stats — set the DC and adjust the "On Success" half-damage yourself.`);
  };

  const save = async () => {
    const body = {
      label: label.trim() || null,
      size: Math.round((parseFloat(sizeFt) / 5) * gridSize),
      rotation: parseFloat(rotation) || 0,
      dc: parseInt(dc, 10) || 0,
      save_type: saveType,
      check_kind: checkKind,
      skill: checkKind === "skill" ? skill : null,
      success_effect: { ...successEffect, message: (successEffect.message || "").trim() || null, damage_dice: (successEffect.damage_dice || "").trim() || null, damage_type: (successEffect.damage_type || "").trim() || null },
      fail_effect: { ...failEffect, message: (failEffect.message || "").trim() || null, damage_dice: (failEffect.damage_dice || "").trim() || null, damage_type: (failEffect.damage_type || "").trim() || null },
      hidden,
    };
    if (!canEditEffect) {
      delete body.success_effect;
      delete body.fail_effect;
      delete body.dc;
      delete body.save_type;
      delete body.check_kind;
      delete body.skill;
      delete body.hidden;
    }
    try {
      const { data } = await api.patch(`/games/${gameId}/templates/${template.id}`, body);
      onSaved?.(data);
      toast.success("Template updated");
      onOpenChange(false);
    } catch {
      toast.error("Could not save template");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${label || "this template"}"? This can't be undone.`)) return;
    await api.delete(`/games/${gameId}/templates/${template.id}`);
    onDeleted?.(template.id);
    onOpenChange(false);
  };

  const resetTriggers = async () => {
    const { data } = await api.patch(`/games/${gameId}/templates/${template.id}`, { reset_triggers: true });
    onSaved?.(data);
    toast.success("Trigger reset — it can catch someone again");
  };

  const trigger = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post(`/games/${gameId}/templates/${template.id}/trigger`);
      if (data.hit_count > 0) toast.success(`${data.hit_token_names.join(", ")} caught in the blast — they're rolling now.`);
      else toast.info("No one was standing in the area.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not trigger this template");
    } finally {
      setTriggering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm max-h-[85vh] overflow-y-auto" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl capitalize">{template.shape} Template</DialogTitle>
          <DialogDescription>Drag it on the map to reposition. {canEditEffect ? "Configure its effect below, then Trigger it when it lands." : "Only the GM can configure this template's effect."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Label</p>
            <Input data-testid="template-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Fireball" className="bg-black/30 border-white/10" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">{template.shape === "circle" ? "Radius" : "Length"} (ft)</p>
              <Input data-testid="template-size-input" type="number" min={5} step={5} value={sizeFt} onChange={(e) => setSizeFt(e.target.value)} className="bg-black/30 border-white/10" />
            </div>
            {template.shape !== "circle" && (
              <div className="flex-1">
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Angle (deg)</p>
                <Input data-testid="template-rotation-input" type="number" value={rotation} onChange={(e) => setRotation(e.target.value)} className="bg-black/30 border-white/10" />
              </div>
            )}
          </div>
          {canEditEffect && (
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <div>
                    <p className="text-xs text-[#f1e0c5]/80">Invisible Trigger</p>
                    <p className="text-[9px] text-[#f1e0c5]/40">Hidden from players — auto-fires when their token walks in, like a trap</p>
                  </div>
                </div>
                <Switch data-testid="template-hidden-switch" checked={hidden} onCheckedChange={setHidden} />
              </div>
              {hidden && (
                <Button data-testid="template-reset-triggers-button" variant="secondary" size="sm" className="w-full rounded-full" onClick={resetTriggers}>
                  Reset Trigger (let it catch someone again)
                </Button>
              )}
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">Load rulebook standards (optional)</p>
              <Select onValueChange={applySpellPreset}>
                <SelectTrigger data-testid="template-spell-preset-select" className="bg-black/30 border-white/10">
                  <SelectValue placeholder="Pick a spell to prefill damage/save..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {spells.filter((s) => s.damage).map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.damage.dice} {s.damage.type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="space-y-1.5 pt-1">
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">What kind of roll?</p>
                <Select value={checkKind} onValueChange={setCheckKind}>
                  <SelectTrigger data-testid="template-check-kind-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHECK_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[9px] text-[#f1e0c5]/30">Not just spell AOEs — draw a Line along a cliff edge or trap corridor and set it to a Skill Check (e.g. Acrobatics to avoid falling) or a flat DC instead of a save.</p>
              </div>
              <div className="flex gap-3 pt-1">
                <div className="flex-1">
                  <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">DC</p>
                  <Input data-testid="template-dc-input" type="number" value={dc} onChange={(e) => setDc(e.target.value)} className="bg-black/30 border-white/10" />
                </div>
                {checkKind === "save" && (
                  <div className="flex-1">
                    <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Save Type</p>
                    <Select value={saveType} onValueChange={setSaveType}>
                      <SelectTrigger data-testid="template-save-type-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SAVE_TYPES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {checkKind === "skill" && (
                  <div className="flex-1">
                    <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Skill</p>
                    <Select value={skill} onValueChange={setSkill}>
                      <SelectTrigger data-testid="template-skill-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SKILLS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <EffectEditor label="On Failed Save" effect={failEffect} onChange={setFailEffect} conditions={conditions} testPrefix="template-fail-effect" />
              <EffectEditor label="On Successful Save" effect={successEffect} onChange={setSuccessEffect} conditions={conditions} testPrefix="template-success-effect" />
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button data-testid="template-save-button" onClick={save} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]">Save</Button>
            <Button data-testid="template-delete-button" variant="secondary" className="rounded-full text-[#D90429]" onClick={remove}>Delete</Button>
          </div>
          {canEditEffect && (
            <Button data-testid="template-trigger-button" onClick={trigger} disabled={triggering} className="w-full rounded-full gap-2 bg-[#7c2d12] hover:bg-[#5c2109]">
              <Flame className="w-4 h-4" /> {triggering ? "Triggering..." : "Trigger Effect"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
